import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Button,
  useMediaQuery,
  useTheme,
  alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { neonBlue, shakespeare, stormGrey, redOrange } from '../../theme/colors';

const gradientBackground = `linear-gradient(135deg, ${neonBlue[600]} 0%, ${neonBlue[500]} 50%, ${shakespeare[500]} 100%)`;

export const AppDialog = ({
  open,
  onClose,
  maxWidth = 'sm',
  fullWidth = true,
  fullScreen: fullScreenProp,
  scroll = 'paper',
  disableEscapeKeyDown = false,
  children,
  ...rest
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const fullScreen = fullScreenProp ?? isMobile;
  const maxHeight = fullScreen ? '100vh' : 'min(90vh, 900px)';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      fullScreen={fullScreen}
      scroll={scroll}
      disableEscapeKeyDown={disableEscapeKeyDown}
      PaperProps={{
        sx: {
          display: 'flex',
          flexDirection: 'column',
          maxHeight,
          borderRadius: fullScreen ? 0 : 2,
          overflow: 'hidden',
          border: `1px solid ${stormGrey[200]}`,
          boxShadow: `0 24px 48px ${alpha(stormGrey[900], 0.14)}`,
          '& > form': {
            display: 'flex',
            flexDirection: 'column',
            flex: '1 1 auto',
            minHeight: 0,
            overflow: 'hidden',
          },
        },
      }}
      {...rest}
    >
      {children}
    </Dialog>
  );
};

export const AppDialogTitle = ({
  children,
  subtitle,
  onClose,
  showClose = true,
  align = 'left',
  variant = 'gradient',
  sx,
  ...rest
}) => {
  const isGradient = variant === 'gradient';

  return (
    <DialogTitle
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 2,
        px: 3,
        py: 2.25,
        textAlign: align,
        flexShrink: 0,
        ...(isGradient
          ? {
              background: gradientBackground,
              color: '#fff',
            }
          : {
              bgcolor: '#fff',
              borderBottom: `1px solid ${stormGrey[200]}`,
            }),
        ...sx,
      }}
      {...rest}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="h6"
          component="span"
          sx={{
            display: 'block',
            fontWeight: 700,
            fontSize: '1.125rem',
            color: isGradient ? '#fff' : stormGrey[900],
          }}
        >
          {children}
        </Typography>
        {subtitle && (
          <Typography
            variant="body2"
            sx={{
              mt: 0.5,
              color: isGradient ? alpha('#fff', 0.85) : stormGrey[500],
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
      {showClose && onClose && (
        <IconButton
          onClick={onClose}
          aria-label="close"
          size="small"
          sx={{
            color: isGradient ? '#fff' : stormGrey[500],
            bgcolor: isGradient ? alpha('#fff', 0.12) : alpha(stormGrey[500], 0.08),
            mt: -0.25,
            mr: -0.5,
            '&:hover': {
              bgcolor: isGradient ? alpha('#fff', 0.22) : alpha(stormGrey[500], 0.14),
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
    </DialogTitle>
  );
};

export const AppDialogContent = ({ children, dividers = true, sx, ...rest }) => (
  <DialogContent
    dividers={dividers}
    sx={{
      px: 3,
      py: 2.5,
      bgcolor: '#fff',
      borderColor: stormGrey[200],
      flex: '1 1 auto',
      minHeight: 0,
      overflowY: 'auto',
      WebkitOverflowScrolling: 'touch',
      '& .MuiOutlinedInput-root': {
        bgcolor: stormGrey[50],
      },
      '& .MuiFormControl-root': {
        mb: 2,
      },
      '& .MuiFormControl-root:last-child': {
        mb: 0,
      },
      ...sx,
    }}
    {...rest}
  >
    {children}
  </DialogContent>
);

export const AppDialogActions = ({ children, sx, ...rest }) => (
  <DialogActions
    sx={{
      px: 3,
      py: 2,
      gap: 1.5,
      flexShrink: 0,
      bgcolor: stormGrey[50],
      borderTop: `1px solid ${stormGrey[200]}`,
      '& .MuiButton-outlined': {
        borderColor: stormGrey[300],
        color: stormGrey[700],
        fontWeight: 600,
        '&:hover': {
          borderColor: stormGrey[400],
          bgcolor: alpha(stormGrey[500], 0.04),
        },
      },
      '& .MuiButton-contained': {
        fontWeight: 600,
        px: 2.5,
        boxShadow: `0 4px 12px ${alpha(neonBlue[500], 0.28)}`,
        '&:hover': {
          boxShadow: `0 6px 16px ${alpha(neonBlue[500], 0.34)}`,
        },
      },
      '& .MuiButton-containedError': {
        boxShadow: `0 4px 12px ${alpha(redOrange[500], 0.28)}`,
        '&:hover': {
          boxShadow: `0 6px 16px ${alpha(redOrange[500], 0.34)}`,
        },
      },
      ...sx,
    }}
    {...rest}
  >
    {children}
  </DialogActions>
);

export const AppDialogCancelButton = (props) => (
  <Button variant="outlined" color="inherit" {...props} />
);

export const AppDialogPrimaryButton = (props) => (
  <Button variant="contained" color="primary" {...props} />
);

export const AppDialogState = ({
  icon,
  title,
  message,
  detail,
  children,
  sx,
}) => (
  <AppDialogContent dividers={false} sx={{ textAlign: 'center', ...sx }}>
    <Box sx={{ py: 2.5, px: 1 }}>
      {icon && (
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            mx: 'auto',
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: gradientBackground,
            color: '#fff',
            boxShadow: `0 8px 24px ${alpha(neonBlue[500], 0.3)}`,
          }}
        >
          {icon}
        </Box>
      )}
      {title && (
        <Typography variant="h6" sx={{ fontWeight: 700, color: stormGrey[900], mb: 1 }}>
          {title}
        </Typography>
      )}
      {message && (
        <Typography variant="body2" sx={{ color: stormGrey[600] }}>
          {message}
        </Typography>
      )}
      {detail && (
        <Typography variant="body2" sx={{ color: stormGrey[500], mt: 1 }}>
          {detail}
        </Typography>
      )}
      {children}
    </Box>
  </AppDialogContent>
);

export default AppDialog;
